import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapPolygon } from '@angular/google-maps';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';

import { DefaultIcon } from '@summit/core';
import { EPosition, SummitDisposing } from '@summit/models';
import { ISMapPolygon } from '../../models/i-map-polygon';
import { ISMapPolyLine } from '../../models/i-s-map-polyLine';
import { SMapMarker } from '../../models/s-map-marker';
import { SMapPolygon } from '../../models/s-map-polygon';
import { SMapPolygonGoogle } from '../../models/s-map-polygon-google';
import { SMapService } from '../../services/s-map.service';

const defaultLat = 31.51073;
const defaultLng = -96.4247;
//const defaultZoom = 15;

@Component({
  selector: 's-map-view',
  templateUrl: './map-view.component.html',
  styleUrls: ['./map-view.component.scss'],
  standalone: true,
  imports: [CommonModule, GoogleMapsModule]
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapViewComponent extends SummitDisposing implements OnInit, AfterViewInit {
  @Input() point$: Observable<EPosition | null> = of(null);
  @Input() lines$: Observable<ISMapPolyLine[]> = of(new Array<ISMapPolyLine>());
  @Input() overlay$: Observable<ISMapPolygon[]> = of(new Array<ISMapPolygon>());
  @Input() markers$: Observable<SMapMarker[]> = of([]);
  @Input() markersIncludedInCalc = false;
  @Input() streetView = false;
  @Input() inputControl: boolean | undefined;
  @Input() zoomControl: boolean | undefined;
  @Input() fullscreenControl: boolean | undefined;
  @Input() pointCountLimit = 10000; // Position report count limit

  private forceShowMapBS = new BehaviorSubject<boolean>(false);
  totalPointCount$: Observable<number>;
  shouldShowMapButton$: Observable<boolean>;
  private mapPolygons: { [key: string]: google.maps.Polygon } = {};
  private infoWindow: MapInfoWindow | undefined;
  @ViewChild('viewPort') viewPort: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('map') map: GoogleMap | undefined; //Live loading after ngIf
  @ViewChildren('mapInfo') mapInfoList: QueryList<MapInfoWindow> | undefined; //Live loading after ngIf
  lclMapData$: Observable<Array<any>> | undefined;
  options$: Observable<google.maps.MapOptions> = of({});
  infoContent = 'Hello';
  drawingManager: google.maps.drawing.DrawingManager | undefined;
  polygon: EPosition[];
  lclOverlay$: Observable<SMapPolygonGoogle[]>;
  @Output() newPolygon = new EventEmitter<EPosition[]>();
  @Output() deletePolygon = new EventEmitter<string>();
  showPlaceHolder$: Observable<boolean> | undefined;
  defaultCenter: { lat: number; lng: number } | undefined;
  defaultZoom: number | undefined;
  redrawBs = new BehaviorSubject<number>(0);
  redraw$ = this.redrawBs.asObservable().pipe(debounceTime(100));

  defaultIcon = DefaultIcon;

  pointOptions: any = {
    //animation: google.maps.Animation.BOUNCE
    draggable: true,
    icon: '/assets/icons/baseline_flag_white_24dp.png'
  };

  defaultPolygonOptions: google.maps.PolygonOptions = {
    editable: true,
    visible: false,
    draggable: true
  };
  apiLoaded$: Observable<boolean>;
  constructor(
    public mapService: SMapService,
    private el: ElementRef
  ) {
    super();
    this.apiLoaded$ = mapService.loadApi();
  }

  ngOnInit(): void {
    this.zoomControl ??= true;
    this.fullscreenControl ??= true;

    // Calculate total point count from lines only (add 1 to compensate for position reports being 1 more than lines)
    this.totalPointCount$ = this.lines$.pipe(
      map((lines) => {
        if (lines && Array.isArray(lines)) {
          const linePoints = lines.reduce((acc, line) => acc + (line.points?.length || 0), 0);
          return linePoints + 1; // Add 1 to compensate for position reports being 1 more than lines
        }
        return 0;
      }),
      startWith(0),
      shareReplay(1)
    );

    // Determine if we should show the "Show Map" button
    this.shouldShowMapButton$ = combineLatest({
      totalPoints: this.totalPointCount$,
      forceShow: this.forceShowMapBS.asObservable()
    }).pipe(
      map(({ totalPoints, forceShow }) => totalPoints > this.pointCountLimit && !forceShow),
      shareReplay(1)
    );

    this.options$ = combineLatest({
      point: this.point$.pipe(
        distinctUntilChanged(
          (previous, current) => current?.latitude == previous?.latitude && current?.longitude == previous?.longitude
        )
      ),
      markers: this.markers$.pipe(
        //skipWhile(() => !this.markersIncludedInCalc),
        //tap(() => console.log(`Markers Updated with flag set to ${this.markersIncludedInCalc}`)),
        startWith([])
      ),
      lines: this.lines$.pipe(
        //skipWhile(() => !this.markersIncludedInCalc),
        //tap(() => console.log(`Lines Updated with flag set to ${this.markersIncludedInCalc}`)),
        startWith([])
      ),
      overlay: this.overlay$.pipe(
        distinctUntilChanged((previous, current) => {
          const previousTotalLatLng =
            previous
              ?.map((entry) => entry.geometry)
              .reduce((acc, val: EPosition[]) => acc.concat(val), [])
              .reduce((a, b) => (a = a + (b?.latitude ?? 0) + (b?.longitude ?? 0)), 0) ?? 0;
          const currentTotalLatLng =
            current
              ?.map((entry) => entry.geometry)
              .reduce((acc, val: EPosition[]) => acc.concat(val), [])
              .reduce((a, b) => (a = a + (b?.latitude ?? 0) + (b?.longitude ?? 0)), 0) ?? 0;
          return previousTotalLatLng == currentTotalLatLng;
        }),
        startWith([])
      ),
      loaded: this.apiLoaded$,
      redraw: this.redraw$,
      shouldShowButton: this.shouldShowMapButton$
    }).pipe(
      filter((combined) => combined.loaded && !combined.shouldShowButton),
      //tap((data) => console.log('Centered Triggered', data)),
      map((combined) => {
        let latitudes = new Array<number>();
        let longitudes = new Array<number>();
        if (combined.point?.latitude) latitudes.push(combined.point.latitude);
        if (this.markersIncludedInCalc) {
          //console.log('Markers Included in Calc', markers);
          latitudes = latitudes.concat(combined.markers.map((entry) => entry?.point?.latitude));
          longitudes = longitudes.concat(combined.markers.map((entry) => entry?.point?.longitude));
        }
        if (combined.lines && Array.isArray(combined.lines)) {
          const lineLatLong = combined.lines
            ?.map((line: ISMapPolyLine) => line.points)
            .reduce((acc, val: EPosition[]) => acc.concat(val), [])
            .filter((entry) => entry != null); //Flatten 2d Array
          //console.log('Line Lat Long', lineLatLong);
          if (lineLatLong && Array.isArray(lineLatLong)) {
            latitudes = latitudes.concat(lineLatLong.map((entry: EPosition) => entry.latitude));
            longitudes = longitudes.concat(lineLatLong.map((entry: EPosition) => entry.longitude));
          }
        }
        if (combined.overlay && Array.isArray(combined.overlay)) {
          const overLayLatLong = combined.overlay
            ?.map((overlay) => overlay.geometry)
            .reduce((acc, val: EPosition[]) => acc.concat(val), [])
            .filter((entry) => entry != null); //Flatten 2d Array
          if (overLayLatLong && Array.isArray(overLayLatLong)) {
            latitudes = latitudes.concat(overLayLatLong.map((entry: EPosition) => entry.latitude));
            longitudes = longitudes.concat(overLayLatLong.map((entry: EPosition) => entry.longitude));
          }
        }
        if (combined.point?.longitude) longitudes.push(combined.point.longitude);
        //console.log('finding max/min Lat of', latitudes);
        //console.log('finding max/min Lng of', longitudes);
        return {
          latMax: Math.max(...latitudes),
          latMin: Math.min(...latitudes),
          lngMax: Math.max(...longitudes),
          lngMin: Math.min(...longitudes)
        };
      }),
      //tap((entry) => console.log('pre-check:', entry)),
      map((item) => {
        if (!isFinite(item.latMax)) item.latMax = defaultLat;
        if (!isFinite(item.latMin)) item.latMin = defaultLat;
        if (!isFinite(item.lngMax)) item.lngMax = defaultLng;
        if (!isFinite(item.lngMin)) item.lngMin = defaultLng;
        return item;
      }),
      map((item) => {
        if (item.latMax == item.latMin) {
          item.latMax = item.latMax + 0.0001;
          item.latMin = item.latMin - 0.0001;
        }
        if (item.lngMax == item.lngMin) {
          item.lngMax = item.lngMax + 0.0001;
          item.lngMin = item.lngMin - 0.0001;
        }
        return item;
      }),
      //tap((entry) => console.log('pre-options:', entry)),
      //tap(() => console.log('dimensions:', this.el.nativeElement.offsetHeight, this.el.nativeElement.offsetWidth)),
      map((item: any) => ({
        center: {
          lat: (item.latMax + item.latMin) / 2,
          lng: (item.lngMax + item.lngMin) / 2
        },
        zoom: getBoundsZoomLevel(item.latMin, item.lngMin, item.latMax, item.lngMax, {
          height: this.el.nativeElement.offsetHeight,
          width: this.el.nativeElement.offsetWidth
        }),
        mapTypeId: 'hybrid',
        disableDefaultUI: true,
        zoomControl: this.zoomControl,
        streetViewControl: this.streetView,
        fullscreenControl: this.fullscreenControl
      })),
      tap((entry) => {
        this.defaultCenter = { lat: entry.center.lat as number, lng: entry.center.lng as number };
        //console.log('zoom:', entry.zoom);
        this.defaultZoom = entry.zoom as number;
        // console.log(
        //   'element',
        //   this.el.nativeElement.offsetWidth,
        //   this.el.nativeElement.offsetHeight,
        //   this.el.nativeElement
        // );
        // console.log('final:', entry);
      }),
      shareReplay(1)
    );
    this.lclMapData$ = this.markers$
      ? this.markers$.pipe(
          debounceTime(2 * 1000)
          //tap(dataSet=> console.log("dataset:",dataSet))
        )
      : of([]);

    // Process markers and lines when both API is loaded and map is visible
    combineLatest({
      loaded: this.apiLoaded$,
      shouldShowButton: this.shouldShowMapButton$
    })
      .pipe(
        filter(({ loaded, shouldShowButton }) => loaded && !shouldShowButton),
        takeUntil(this.disposing$)
      )
      .subscribe(() => {
        // Add delay to ensure map component is initialized
        setTimeout(() => {
          this.markers$.pipe(take(1)).subscribe((dataSet) => {
            dataSet.forEach((marker) => {
              const markerOptions: google.maps.MarkerOptions = {
                map: this.map?.googleMap ?? null,
                position: marker.point.toLatLng(),
                title: marker.title,
                icon: marker.icon
              };

              if (typeof marker.label === 'string' && marker.label.trim().length > 0) {
                markerOptions.label = {
                  text: marker.label,
                  color: 'black',
                  className: 'trailerMarker'
                };
              }

              new google.maps.Marker(markerOptions);
            });
          });
          this.lines$.pipe(take(1)).subscribe((dataSet) => {
            dataSet.forEach((line) => {
              const lclPoly = new google.maps.Polyline({
                icons: line.icons,
                path: line.points.map((entry) => entry.toLatLng())
              });
              lclPoly.setMap(this.map?.googleMap ?? null);
            });
          });
        }, 100); // 100ms delay to allow map to initialize
      });
    this.lclOverlay$ = this.overlay$.pipe(
      //tap((data) => console.log('Processing Overlay:', data)),
      map((dataSet) => dataSet?.map((aoi) => new SMapPolygonGoogle(aoi)) ?? [])
    );
    //this.showPlaceHolder$ = of(false);

    this.showPlaceHolder$ = this.point$.pipe(
      map((point) => point == null),
      startWith(true),
      shareReplay(1)
    );
    /*
    this.showPlaceHolder$.subscribe((show) => {
      console.log('Show Placeholder', show);
    });
    */
  }

  ngAfterViewInit(): void {
    //Now the children are initialized
    combineLatest({
      items:
        this.mapInfoList?.changes.pipe(startWith(this.mapInfoList?.length > 0 ? this.mapInfoList : null)) ?? of(null),
      loaded: this.apiLoaded$
    })
      .pipe(
        filter((combined) => combined.loaded),
        //tap(test => console.log("MapService is loaded, map list has changes, map info list has changes", test)),
        //reduce((a, b) => a && b ? true : false, true),
        //tap(test => console.log("Processing")),
        take(1)
        //tap(test => console.log("Processing only the first")),
      ) // No Unsubscribe
      .subscribe((results) => {
        this.infoWindow = results.items;
        //console.log('Map:', this.map)
        //Point
        //google.maps.event.trigger(this.map, "resize")
        //console.log('Creating Drawing Manager')
        this.drawingManager = new google.maps.drawing.DrawingManager({
          //drawingMode: google.maps.drawing.OverlayType.POLYGON,
          drawingControl: false,
          drawingControlOptions: {
            position: 9, // google.maps.ControlPosition.TOP_CENTER,
            drawingModes: [
              //google.maps.drawing.OverlayType.MARKER,
              //google.maps.drawing.OverlayType.CIRCLE,
              google.maps.drawing.OverlayType.POLYGON
              //google.maps.drawing.OverlayType.POLYLINE,
              //google.maps.drawing.OverlayType.RECTANGLE,
            ]
          },
          markerOptions: {
            icon: 'https://developers.google.com/maps/documentation/javascript/examples/full/images/beachflag.png'
          },
          circleOptions: {
            fillColor: '#ffff00',
            fillOpacity: 1,
            strokeWeight: 5,
            clickable: false,
            editable: true,
            zIndex: 1
          }
        });
        //console.log('Linking Drawing Manager')
        this.drawingManager?.setMap(this.map?.googleMap ?? null);
        google.maps.event.addListener(this.drawingManager, 'overlaycomplete', (event: any) =>
          this.drawEventHandler(event)
        );
        this.lclOverlay$
          .pipe(
            map((data) => data ?? []),
            takeUntil(this.disposing$)
          )
          .subscribe((data: SMapPolygonGoogle[]) => {
            Object.keys(this.mapPolygons).forEach((key: string) => {
              //console.log(`Checking for deleted Polygons`, data);
              const currentIndex = data.findIndex((item) => item.id == key);
              if (currentIndex == -1 || data[currentIndex].boundary == null) {
                const removePoly = this.mapPolygons[key];
                removePoly.unbindAll();
                removePoly.setMap(null);
                delete this.mapPolygons[key];
              }
            });
            //console.log(`Adding Polygons to map`, data);
            data.forEach((mapPoly: SMapPolygonGoogle) => {
              //console.log(`Looking up Polygon ${mapPoly.id}`);
              const existingPoly = this.mapPolygons[mapPoly.id];
              if (existingPoly) {
                console.warn(`Updating Poly: ${mapPoly.id} - Not Implemented`);
              } else if (mapPoly.boundary != null) {
                //console.log(`Adding Poly: ${mapPoly.id}`);
                const newPoly = new google.maps.Polygon();
                const lclPolygonOptions = Object.assign({}, this.defaultPolygonOptions);
                lclPolygonOptions.visible = data?.length > 0 ? true : false;
                lclPolygonOptions.paths = mapPoly.boundary;
                //console.log(`Setting Polygon Options to:`, lclPolygonOptions);
                try {
                  newPoly.setOptions(lclPolygonOptions);
                } catch (e) {
                  console.warn(`Error setting options on ${mapPoly.id}`, e);
                }
                newPoly.setMap(this.map?.googleMap ?? null);
                //console.log(`Adding Poly: ${mapPoly.id}`, newPoly);
                newPoly.addListener('rightclick', (event: any) => this.deleteHandler(event, mapPoly.id));
                newPoly.addListener('click', (event: any) => this.showInfo(event, mapPoly.id));
                this.mapPolygons[mapPoly.id] = newPoly;
              }
            });
            //console.log(`Adding ${data?.length} points to map`);
            //console.log(`Setting polygon options to:`, lclPolygonOptions);
          });
      });
  }

  centerMap() {
    if (this.defaultCenter) this.map?.googleMap?.setCenter(this.defaultCenter);
    if (this.defaultZoom) this.map?.googleMap?.setZoom(this.defaultZoom);
    this.redrawBs.next(this.redrawBs.value + 1);
  }
  drawEventHandler(event: any) {
    //console.log('Draw Complete:', event)
    switch (event.type) {
      case 'circle':
        // Rule fix var circle = event.overlay;
        // Rule fix var center = circle.getCenter();
        // Rule fix var radius = circle.getRadius();
        //console.log(`Circle: Center = Lat:${center.lat()}, Lng: ${center.lng()} with R = ${radius}`)
        break;
      case 'polygon': {
        const polygon = event.overlay as MapPolygon;
        const gPoints = polygon.getPath().getArray();
        this.polygon = gPoints.map(
          (entry) =>
            new EPosition({
              latitude: entry.lat(),
              longitude: entry.lng()
            })
        );
        this.newPolygon.emit(this.polygon);
        //console.log('Polygon: ', this.polygon);
        break;
      }
      default:
        break;
    }
    event.overlay.setMap(null);
    this.drawingManager?.setDrawingMode(null);
  }

  openInfo(marker: any, _content: string) {
    //this.infoContent = content;
    this.infoWindow?.open(marker);
  }

  drawPolygon() {
    this.drawingManager?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
  }
  getPolygons(): Array<SMapPolygon> | null {
    const response = new Array<SMapPolygon>();
    Object.keys(this.mapPolygons).forEach((key) => {
      const lclPoly = this.mapPolygons[key];
      const lclBoundary = lclPoly.getPath();
      const lclBoundaryArray = lclBoundary?.getArray();
      response.push(
        new SMapPolygon({
          id: key,
          geometry: lclBoundaryArray?.map((entry) => new EPosition({ latitude: entry.lat(), longitude: entry.lng() }))
        })
      );
    });
    return response;
  }

  showInfo(event: any, id: string) {
    //console.log(`Click Event`, event);
    // Rule fix var lclPoly = this.mapPolygons[id];
    const infoWindow = new google.maps.InfoWindow();
    this.lclOverlay$.pipe(take(1), takeUntil(this.disposing$)).subscribe((overlay) => {
      const entries = overlay.filter((w) => w.id == id);
      if (entries.length > 0) {
        infoWindow.setContent(`${entries[0].name} [${id?.substring(id.length - 4)}]`);
        infoWindow.setPosition(event.latLng);
        infoWindow.open(this.map?.googleMap ?? null);
      }
    });
  }

  deleteHandler(event: any, id: string) {
    //console.log('Got Right Trigger Event', event);
    if (event.path != null && event.vertex != null) {
      //console.log(`Looking up in`, this.mapPolygons);
      // Rule fix var found = false;
      const lclPoly = this.mapPolygons[id];
      const lclBoundary = lclPoly.getPath();
      if (event.vertex < lclBoundary.getLength()) {
        const potentialVertex = lclBoundary.getAt(event.vertex);
        if (potentialVertex.lat() == event.latLng?.lat() && potentialVertex.lng() == event.latLng?.lng()) {
          //console.log('Deleting', event);
          if (lclBoundary.getLength() > 3) {
            lclBoundary.removeAt(event.vertex);
          }
          // Rule fix found = true;
        }
      }
    } else {
      //console.log(`Removing Polygon`, this.mapPolygons);
      this.deletePolygon.emit(id);
    }
  }

  getViewWindow(): EPosition[] {
    const bounds = this.map?.getBounds();
    if (!bounds) return [];
    //console.log("Bounds: ", bounds)
    const nePoint = bounds.getNorthEast();
    const swPoint = bounds.getSouthWest();
    return [
      new EPosition({ latitude: nePoint.lat(), longitude: nePoint.lng() }),
      new EPosition({ latitude: nePoint.lat(), longitude: swPoint.lng() }),
      new EPosition({ latitude: swPoint.lat(), longitude: swPoint.lng() }),
      new EPosition({ latitude: swPoint.lat(), longitude: nePoint.lng() }),
      new EPosition({ latitude: nePoint.lat(), longitude: nePoint.lng() })
    ];
  }

  public updateControls(zoomControl: boolean, streetViewControl: boolean, fullscreenControl: boolean) {
    this.zoomControl = zoomControl ?? this.zoomControl;
    this.streetView = streetViewControl ?? this.streetView;
    this.fullscreenControl = fullscreenControl ?? this.fullscreenControl;
    this.redrawBs.next(this.redrawBs.value + 1);
  }

  showMap(): void {
    this.forceShowMapBS.next(true);
    this.redrawBs.next(this.redrawBs.value + 1); // increment redraw counter to trigger a map redraw
  }
}

function getBoundsZoomLevel(latMin: number, lngMin: number, latMax: number, lngMax: number, mapDim: any) {
  //console.log(`Getting Bounds Zoom Level for:`, latMin, lngMin, latMax, lngMax, mapDim);
  if (latMin == defaultLat && latMax == defaultLat && lngMax == defaultLng && lngMax == defaultLng) {
    return 3;
  }

  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 17; //= 21;
  //17 zoom should be max for hubs.

  const neLat = latMax;
  const neLng = lngMax;
  const swLat = latMin;
  const swLng = lngMin;

  if (mapDim.height == 0) mapDim.height = mapDim.width;

  const latFraction = (latRad(neLat) - latRad(swLat)) / Math.PI;
  //console.log(`LatFraction: ${latFraction}`);

  const lngDiff = neLng - swLng;
  //console.log(`LngDiff: ${lngDiff}`);
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  //console.log(`LatZoom: ${latZoom}, LngZoom: ${lngZoom}`);
  return Math.min(latZoom, lngZoom, ZOOM_MAX - 1) * 0.98; //Hack to get space around frame
}

function latRad(lat: number) {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
}

function zoom(mapPx: number, worldPx: number, fraction: number) {
  return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
}
